import type { FocusEventHandler, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { AlertTypes } from '../../../AlertBadge'
import { AlertBadge } from '../../../AlertBadge'
import { AnimationWrapper } from '../../../AnimationWrapper'
import type { BadgeTheme } from '../../../Badge'
import type { ProductColour } from '../../../ColourSwatch/ColourSwatchGroup'
import { ColourSwatchGroup } from '../../../ColourSwatch/ColourSwatchGroup'
import { BaseLink } from '../../../Links'
import type { PictureProps } from '../../../Picture'
import { Picture } from '../../../Picture'
import type { ProductScoreGaugeProps } from '../../../ProductScoreGauge'
import { ProductScoreGauge } from '../../../ProductScoreGauge'
import { SaveButton } from '../../../SaveButton'
import { TypographyV2 } from '../../../Seatbelt2/Foundations/Typography'
import { PlaceholderImage } from '../shared/PlaceholderImage/PlaceholderImage'
import { ProductCardBadges } from '../shared/ProductCardBadges'
import type { CCBProps } from '../shared/ProductCardCompareCheckbox/ProductCardCompareCheckbox'
import { ProductCardCompareCheckbox } from '../shared/ProductCardCompareCheckbox/ProductCardCompareCheckbox'
import { ProductCardPrice } from '../shared/ProductCardPrice/ProductCardPrice'
import { ProductCardTags } from '../shared/ProductCardTags/ProductCardTags'
import styles from './ProductCardPortrait.module.scss'

export const ProductCardPortrait: FunctionComponent<
  ProductCardPortraitProps
> = ({
  className,
  image,
  manufacturer,
  model,
  badges,
  tags,
  price,
  slug,
  retailers,
  alertType,
  handleAddCompare,
  handleRemoveCompare,
  handleFocus,
  productScoreGauge,
  isSelectedForComparison,
  comparisonLimitReached,
  cardCallback,
  productUrl,
  taxonomySlug,
  businessKey,
  comparisonIsEnabled,
  hasBadgeZone = false,
  variants,
  selectedVariant,
  handleVariantChange,
  saved = false,
  saveOnClick,
  handleCompareWhenDisabled,
}) => {
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <article
      data-testid="sb-product-portrait-card"
      onClick={cardCallback}
      onKeyDown={(e) => e.key === 'Enter' && cardCallback(e)}
      onFocus={(e) => handleFocus && handleFocus(e)}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      className={classnames(className, styles.card)}
      data-url={`Find this product at this URL: (${productUrl})`}
    >
      {((badges && badges?.length > 0) || hasBadgeZone) && (
        <div className={styles.badgeZone}>
          <ProductCardBadges
            badges={badges}
            className={styles.productCardBadges}
          />
        </div>
      )}

      <div className={styles.information}>
        <div className={styles.imageContainer}>
          <AlertBadge type={alertType} className={styles.safetyAlert} />
          {image?.src ? (
            <Picture {...image} className={styles.image} />
          ) : (
            <PlaceholderImage />
          )}
        </div>
        <div className={styles.details} data-testid="product-details">
          {saveOnClick && (
            <div className={styles.saveButton}>
              <SaveButton
                selected={saved}
                onClick={saveOnClick}
                productName={`${manufacturer} ${model}`}
              />
            </div>
          )}
          <BaseLink
            href={`/reviews/${taxonomySlug}/${slug}`}
            invertedUnderline={true}
            onClick={(e: React.MouseEvent) => e.preventDefault()}
            tabIndex={-1}
            className={styles.link}
          >
            <TypographyV2
              tag="h3"
              textStyle="sb-text-interface-heading-large"
              className={styles.manufacturer}
            >
              <AnimationWrapper inverted={true}>
                {manufacturer}
              </AnimationWrapper>
            </TypographyV2>
            <TypographyV2
              textStyle="sb-text-interface-body-default-regular"
              className={styles.model}
            >
              {model}
            </TypographyV2>
          </BaseLink>
          <ProductCardTags tags={tags} className={styles.tags} />
          {variants && handleVariantChange && (
            <div className={styles.colourSwatch}>
              <ColourSwatchGroup
                name={`colour-swatch-group-${businessKey}`}
                variants={variants}
                selectedVariant={selectedVariant as ProductColour[]}
                onChange={handleVariantChange}
              />
            </div>
          )}
          <ProductCardPrice
            {...{ price, productUrl, retailers, slug, manufacturer, model }}
            className={styles.price}
          />
        </div>
      </div>
      <div>
        <div className={styles.score}>
          <ProductScoreGauge
            {...productScoreGauge}
            className={styles.productScoreGauge}
            variant="small-landscape"
          />
        </div>
      </div>
      {comparisonIsEnabled && (
        <ProductCardCompareCheckbox
          className={styles.compare}
          handleAddCompare={handleAddCompare}
          handleRemoveCompare={handleRemoveCompare}
          handleCompareWhenDisabled={handleCompareWhenDisabled}
          manufacturer={manufacturer}
          model={model}
          slug={slug}
          businessKey={businessKey}
          isSelectedForComparison={isSelectedForComparison}
          comparisonLimitReached={comparisonLimitReached}
          type="product-card-portrait"
        />
      )}
    </article>
  )
}

///////// IMPLEMENTATION /////////

export type ProductCardPortraitProps = {
  className?: string
  image: PictureProps
  manufacturer: string
  model: string
  price: string
  slug: string
  handleFocus?: FocusEventHandler<HTMLElement>
  productScoreGauge: ProductScoreGaugeProps
  badges?: Array<BadgeTheme> | null
  tags?: Array<string> | null
  retailers?: boolean
  alertType?: AlertTypes | null
  cardCallback: (
    e: React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent
  ) => void
  productUrl: string
  taxonomySlug: string
  businessKey: string
  comparisonIsEnabled?: boolean
  hasBadgeZone?: boolean
  variants?: ProductColour[][] | null
  selectedVariant?: ProductColour[] | null
  handleVariantChange?: (selectedVariant: ProductColour[]) => void
  saved: boolean
  saveOnClick?: () => void
} & Pick<
  CCBProps,
  | 'comparisonLimitReached'
  | 'handleAddCompare'
  | 'handleRemoveCompare'
  | 'isSelectedForComparison'
  | 'handleCompareWhenDisabled'
>
